import 'reflect-metadata';
import { inject, injectable } from 'inversify';
import {
  publication_book_all,
  publication_book_dto,
  publication_book_full,
  publication_book_last_update,
} from '@Api';
import { Book, BookVersioning, BuildStatus } from '@Models';
import { IMapper } from '../IMapper';
import { TYPES } from '@Types';
import { BookSectionMapper } from './book-section.mapper';

@injectable()
export class BookMapper
  implements IMapper<string, publication_book_all, publication_book_full, Book>
{
  @inject(TYPES.Mapper.BookSectionMapper)
  private _sectionMapper!: BookSectionMapper;

  public mapAll(item: publication_book_all): Book {
    return {
      id: item.id,
      name: item.name,
      versionId: item.version_id,
      armyId: item.army_id,
      armyName: item.army_name,
      armyLogo: item.army_logo,
      updatedAt: item.updated_at,
      sections: [],
      buildAt: null,
      buildUrl: null,
      buildStatus: BuildStatus.NotBuilt,
    } satisfies Book as Book;
  }

  public mapFull(item: publication_book_full): Book {
    return {
      id: item.id,
      name: item.name,
      versionId: item.version_id,
      armyId: item.army_id,
      armyName: item.army_name,
      armyLogo: item.army_logo,
      updatedAt: item.updated_at,
      sections:
        item.sections?.map((section) => this._sectionMapper.map(section)) ?? [],
      buildAt: item.executed_at,
      buildUrl: item.build_url,
      buildStatus: this.mapBuildStatus(item.build_status),
    } satisfies Book as Book;
  }

  public map(item: publication_book_full): Book {
    return {
      id: item.id,
      name: item.name,
      versionId: item.version_id,
      armyId: item.army_id,
      armyName: item.army_name,
      armyLogo: item.army_logo,
      updatedAt: item.updated_at,
      sections:
        item.sections?.map((section) => this._sectionMapper.map(section)) ?? [],
      buildAt: item.executed_at,
      buildUrl: item.build_url,
      buildStatus: this.mapBuildStatus(item.build_status),
    } satisfies Book as Book;
  }

  public mapBuildStatus(item: publication_book_full.build_status): BuildStatus {
    switch (item) {
      case publication_book_full.build_status.BUILDING:
        return BuildStatus.Building;
      case publication_book_full.build_status.BUILT:
        return BuildStatus.Built;
      case publication_book_full.build_status.FAILED:
        return BuildStatus.Failed;
    }
    return BuildStatus.NotBuilt;
  }

  public mapDto(item: Book): publication_book_dto {
    return {
      name: item.name,
      version_id: item.versionId,
      army_id: item.armyId,
    };
  }

  public mapVersioning(item: publication_book_last_update): BookVersioning {
    return {
      id: item.id,
      name: item.name,
      updatedAt: item.updated_at,
    } satisfies BookVersioning as BookVersioning;
  }
}
